import { useEffect, useState } from 'react';

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
          };
          start_param?: string;
        };
        ready: () => void;
        expand: () => void;
        close: () => void;
        openTelegramLink: (url: string) => void;
        openLink: (url: string) => void;
        openInvoice?: (url: string, callback: (status: string) => void) => void;
        HapticFeedback?: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
        };
      };
    };
  }
}

export function useTelegram() {
  const [isReady, setIsReady] = useState(false);
  const [initData, setInitData] = useState<string>('');
  const [user, setUser] = useState<{ id: number; first_name: string; username?: string } | null>(null);
  const [startParam, setStartParam] = useState<string | undefined>(undefined);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      try {
        tg.ready();
        tg.expand();
      } catch (e) {
        console.warn('Failed to initialize Telegram WebApp', e);
      }

      if (tg.initData) {
        setInitData(tg.initData);
        setUser(tg.initDataUnsafe?.user || null);
        setStartParam(tg.initDataUnsafe?.start_param);
        setIsReady(true);
        return;
      }
    }

    // Fallback for standalone browser testing / local dev
    const mockInitData = 'dev_mock_user_123456';
    setInitData(mockInitData);
    setUser({ id: 88888888, first_name: 'Nova Pioneer', username: 'novapioneer' });
    setIsReady(true);
  }, []);

  const triggerHaptic = (style: 'light' | 'medium' | 'heavy' = 'medium') => {
    try {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style);
    } catch {
      // ignore
    }
  };

  const openInvoice = (url: string): Promise<string> => {
    return new Promise((resolve) => {
      const tg = window.Telegram?.WebApp;
      if (tg?.openInvoice) {
        tg.openInvoice(url, (status) => {
          resolve(status);
        });
      } else {
        window.open(url, '_blank');
        resolve('opened_external');
      }
    });
  };

  return {
    isReady,
    initData,
    user,
    startParam,
    triggerHaptic,
    openInvoice,
    openTelegramLink: (url: string) => window.Telegram?.WebApp?.openTelegramLink(url) || window.open(url, '_blank'),
  };
}
